import { FormEvent } from "react";
import { Button, Image, Input } from "@atoms";
import { AuthSection } from "@organisms";
import { useTranslation } from "react-i18next";

export interface FormValues {
  description: string;
  organization: string;
  phoneNumber: string;
  website: string;
}

export interface FormErrorValues {
  description: string | undefined;
  organization: string | undefined;
  phoneNumber: string | undefined;
  website: string | undefined;
}

export type WelcomePageTemplateProps = {
  newPartnerInfo: any;
  values: FormValues;
  errors: FormErrorValues;
  loading: boolean;
  handleBackButtonClick: () => Promise<void>;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: (e?: FormEvent<HTMLFormElement> | undefined) => void;
};

export const WelcomePageTemplate = ({
  values,
  newPartnerInfo,
  errors,
  loading,
  handleBackButtonClick,
  handleInputChange,
  handleSubmit,
}: WelcomePageTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  function onKeyDown(keyEvent: any) {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault();
      handleSubmit();
    }
  }

  return (
    <div className="min-h-screen flex">
      <AuthSection />

      {/* Right side - Welcome form */}
      <div className="w-full lg:w-1/2 min-h-screen flex items-center justify-center bg-white">
        <div className="w-full max-w-md px-8">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {t("auth.welcome.title")}
            </h1>
            <p className="text-gray-600 text-sm">
              {t("auth.welcome.subtitle")}
            </p>
          </div>

          {/* Welcome form */}
          <form onSubmit={handleSubmit} onKeyDown={onKeyDown} className="space-y-4">
            <Input
              variant="text"
              name="organization"
              onChange={handleInputChange}
              placeholder={t("auth.welcome.organization")}
              value={values.organization}
              errorMessage={errors.organization}
            />
            <Input
              variant="text"
              name="website"
              onChange={handleInputChange}
              placeholder={t("auth.welcome.website")}
              value={values.website}
              errorMessage={errors.website}
            />
            <Input
              variant="text"
              name="phoneNumber"
              onChange={handleInputChange}
              placeholder={t("auth.welcome.phoneNumber")}
              value={values.phoneNumber}
              errorMessage={errors.phoneNumber}
            />
            <Input
              variant="textArea"
              name="description"
              onChange={handleInputChange}
              placeholder={t("auth.welcome.description")}
              value={values.description}
              errorMessage={errors.description}
              height={128}
            />
            {/* Create account button */}
            <Button
              type="submit"
              variant="primary"
              label={t("auth.welcome.submit")}
              stretch
              color="neutral-darkest"
              hoverColor="neutral-dark"
              textColor="white"
              loading={loading}
            />
            {/* Back button */}
            <Button
              variant="secondary"
              label={t("auth.common.backToSignIn")}
              color="#ffffff"
              stretch
              brightness={500}
              onClick={handleBackButtonClick}
              size="sm"
            />
          </form>
        </div>
      </div>
    </div>
  );
};
